"use client";
import * as React from "react";
import { cn } from "@/lib/utils";
import type { Country, Value } from "react-phone-number-input";
import { getCountryCallingCode } from "react-phone-number-input";
import originalFlags from "react-phone-number-input/flags";
import { SaudiArabiaFlag } from "@/components/ui/sa-flag";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

// NOTE:
// This component is only used from client components (modals/pages with "use client").
// Using a normal import avoids cases where the dynamic import placeholder looks like an input
// but isn't actually editable yet.
import PhoneInputComponent from "react-phone-number-input";
import { useLocale } from "next-intl";

const flags = { ...originalFlags, SA: SaudiArabiaFlag } as typeof originalFlags;

type CountrySelectOption =
  | Country
  | {
      value: Country;
      label: string;
    };

type CountrySelectWithFlagsProps = {
  value?: Country;
  onChange: (country?: Country) => void;
  options: CountrySelectOption[];
  labels?: Record<string, string>;
  disabled?: boolean;
  readOnly?: boolean;
  tabIndex?: number;
  "aria-label"?: string;
};

function CountrySelectWithFlags({
  value,
  onChange,
  options,
  labels,
  disabled,
  readOnly,
  tabIndex,
  "aria-label": ariaLabel,
}: CountrySelectWithFlagsProps) {
  const [open, setOpen] = React.useState(false);
  const [countryQuery, setCountryQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  // Tracks whether the in-dropdown search field currently holds focus. On mobile,
  // focusing it opens the virtual keyboard which makes Radix Select request a
  // close; we veto that so the list stays open while searching.
  const searchFocusedRef = React.useRef(false);
  // Set when a close is legitimate (outside tap or country selection) so we don't
  // accidentally veto it even while the search field is focused.
  const allowCloseRef = React.useRef(false);

  const selected = (value ?? "ZZ") as unknown as string;
  const safeLabels = labels ?? {};

  // On touch / coarse-pointer devices, auto-focusing the search input pops the
  // virtual keyboard. The keyboard resizes the viewport which makes Radix Select
  // reposition/close, so the list disappears before a country can be tapped.
  // Only auto-focus the search input on devices with a fine pointer (mouse).
  React.useEffect(() => {
    if (!open) return;
    const canHover =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!canHover) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const normalizedOptions = (options ?? [])
    .map((opt) => {
      if (typeof opt === "string") {
        const v = opt as unknown as string;
        return v ? { value: v, label: safeLabels[v] ?? v } : null;
      }
      const v = opt.value as unknown as string;
      if (!v) return null;
      return { value: v, label: opt.label ?? safeLabels[v] ?? v };
    })
    .filter(
      (x): x is { value: string; label: string } =>
        x !== null && Boolean(x.value) && typeof x.label === "string",
    );

  const selectedLabel =
    normalizedOptions.find((o) => o.value === selected)?.label ??
    safeLabels[selected] ??
    selected;

  const isSelectedInternational = selected === "ZZ";
  const selectedCallingCode =
    !isSelectedInternational && selected
      ? `+${getCountryCallingCode(selected as unknown as Country)}`
      : "";

  const SelectedFlag = (flags as Record<string, React.ComponentType<any>>)[
    selected
  ];

  const callingCodeByCountry = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const { value: countryCode } of normalizedOptions) {
      if (!countryCode || countryCode === "ZZ") continue;
      try {
        map[countryCode] =
          `+${getCountryCallingCode(countryCode as unknown as Country)}`;
      } catch {
        // ignore invalid country codes
      }
    }
    return map;
  }, [normalizedOptions]);

  const filteredOptions = React.useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return normalizedOptions;

    return normalizedOptions.filter(({ value: countryCode, label }) => {
      const callingCode = callingCodeByCountry[countryCode] ?? "";
      return (
        label.toLowerCase().includes(q) ||
        countryCode.toLowerCase().includes(q) ||
        callingCode.toLowerCase().includes(q)
      );
    });
  }, [callingCodeByCountry, countryQuery, normalizedOptions]);

  return (
    <Select
      value={selected}
      disabled={disabled}
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          allowCloseRef.current = false;
          setOpen(true);
          setCountryQuery("");
          return;
        }
        // Veto keyboard-induced closes: the search field is focused (mobile
        // keyboard is up) and this close wasn't triggered by an outside tap or a
        // country selection.
        if (searchFocusedRef.current && !allowCloseRef.current) return;
        allowCloseRef.current = false;
        setOpen(false);
      }}
      onValueChange={(next) => {
        if (readOnly) return;
        // A selection is a legitimate reason to close.
        allowCloseRef.current = true;
        onChange(next === "ZZ" ? undefined : (next as Country));
      }}
    >
      <SelectTrigger
        className={cn(
          "h-full w-auto border-0 bg-transparent shadow-none px-0 py-0 focus-visible:ring-0 ",
          "mr-2",
        )}
        aria-label={ariaLabel ?? "Phone number country"}
        tabIndex={tabIndex}
      >
        <span className="flex items-center gap-2">
          <span className="inline-flex  items-center justify-center overflow-hidden   ">
            {SelectedFlag ? (
              <SelectedFlag className="size-9" title={selectedLabel} />
            ) : (
              <span className="text-muted-foreground text-[10px] leading-none">
                {isSelectedInternational ? "INT" : ""}
              </span>
            )}
          </span>
          <span className="text-base tabular-nums font-medium">
            {selectedCallingCode}
          </span>
        </span>
      </SelectTrigger>

      <SelectContent
        align="start"
        className="min-w-[18rem] z-[3000] "
        dir="ltr"
        onPointerDownOutside={() => {
          // Genuine tap outside the dropdown should close it.
          allowCloseRef.current = true;
        }}
        onKeyDown={(e) => {
          if (
            e.target instanceof HTMLElement &&
            e.target.closest("[data-country-search-input]")
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div className="sticky top-0 z-[3000] bg-popover p-1 pb-2 ">
          <Input
            ref={searchInputRef}
            data-country-search-input
            value={countryQuery}
            onFocus={() => {
              searchFocusedRef.current = true;
            }}
            onBlur={() => {
              searchFocusedRef.current = false;
            }}
            onChange={(e) => {
              const v = e.target.value;
              setCountryQuery(v);
              // Refocus after React re-render so Dialog/Radix focus management doesn't steal it
              requestAnimationFrame(() => {
                searchInputRef.current?.focus();
              });
            }}
            placeholder="Search country…"
            className="h-8"
            dir="ltr"
            onKeyDown={(e) => {
              // Keep focus in search: prevent Radix typeahead and arrow-key navigation
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                return;
              }
              e.stopPropagation();
            }}
            onKeyDownCapture={(e) => e.stopPropagation()}
            onKeyUpCapture={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </div>

        {filteredOptions.length === 0 ? (
          <div className="px-2 py-2 text-sm text-muted-foreground">
            No countries found.
          </div>
        ) : null}

        {filteredOptions.map(({ value: countryCode, label }) => {
          const isInternational = !countryCode || countryCode === "ZZ";

          const CallingCode = !isInternational
            ? (callingCodeByCountry[countryCode] ?? "")
            : "";

          const Flag = (flags as Record<string, React.ComponentType<any>>)[
            countryCode
          ];

          return (
            <SelectItem key={countryCode} value={countryCode}>
              <span className="flex w-full items-center gap-2 ">
                {Flag ? (
                  <Flag className="size-8" title={label} />
                ) : (
                  <span className="text-muted-foreground text-[10px] leading-none">
                    {isInternational ? "INT" : ""}
                  </span>
                )}
                <span className="flex-1 truncate">{label}</span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {CallingCode}
                </span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface PhoneInputProps {
  value?: Value;
  onChange?: (value: Value) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  defaultCountry?: Country;
  /** When true, lock to defaultCountry and hide the country picker (ASP HideCountriesList). */
  hideCountrySelect?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  placeholder = "5XXXXXXXXX",
  disabled = false,
  className,
  id,
  name,
  defaultCountry = "SA", // Saudi Arabia as default
  hideCountrySelect = false,
}: PhoneInputProps) {
  const isRtl = useLocale() === "ar";
  // Wrapper to convert E164Number to string
  const handleChange = React.useCallback(
    (phoneValue: Value) => {
      onChange?.(phoneValue);
    },
    [onChange],
  );

  return (
    <div dir="ltr" className="relative w-full min-w-0 overflow-hidden py-0">
      <PhoneInputComponent
        defaultCountry={defaultCountry}
        flags={flags}
        countrySelectComponent={
          hideCountrySelect
            ? () => (
                <span className="mr-2 flex items-center gap-2 text-base tabular-nums font-medium">
                  +{getCountryCallingCode(defaultCountry)}
                </span>
              )
            : CountrySelectWithFlags
        }
        countries={hideCountrySelect ? [defaultCountry] : undefined}
        countryCallingCodeEditable={false}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        id={id}
        name={name}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "[&>input]:border-0 [&>input]:bg-transparent [&>input]:outline-none [&>input]:ring-0 [&>input]:flex-1 [&>input]:min-w-0",
          "[&>input]:focus-visible:outline-none [&>input]:focus-visible:ring-0",
          "[&>input]:text-left [&>input]:[direction:ltr]",
          className,
          isRtl &&
            "[&>input]:placeholder-shown:text-right [&>input]:placeholder-shown:[direction:rtl] [&>input]:placeholder:text-right [&>input]:placeholder:[direction:rtl] [&>input]:placeholder:[unicode-bidi:isolate]",
        )}
        numberInputProps={{
          className: cn(
            "border-0 bg-transparent outline-none ring-0 focus-visible:ring-0 flex-1 min-w-0 placeholder:text-slate-500 placeholder:opacity-100 [direction:ltr] text-left",
            isRtl &&
              "placeholder-shown:text-right placeholder-shown:[direction:rtl] placeholder:text-right placeholder:[direction:rtl] placeholder:[unicode-bidi:isolate]",
          ),
        }}
      />
    </div>
  );
}

